import { useCallback } from "react";

import type { ImageDto } from "@/api/types";

export type FormImage = ImageDto | InputFile;

interface InputFile {
  file: File;
  url: string;
  uploadPromise?: Promise<ImageDto | undefined>;
}

interface Props {
  selectedImages: FormImage[];
  maximumFiles?: number;
  onChange: (images: FormImage[]) => void;
}

interface ImageInput {
  readonly addImage: (file: File) => void;
  readonly addImages: (files: FileList | null) => void;
  readonly replaceImage: (oldImage: FormImage, newFile: File) => void;
  readonly removeImage: (imageToRemove: FormImage | undefined) => void;
  readonly removeImages: () => void;
}

export function useImageInput({ selectedImages, maximumFiles = 1, onChange }: Props): ImageInput {
  const removeImage = useCallback(
    (imageToRemove: FormImage | undefined) => {
      if (!imageToRemove) {
        return;
      }

      const newSelectedImages = [];

      for (const image of selectedImages) {
        if (imageToRemove.url === image.url) {
          revokeFileUrl([image]);
        } else {
          newSelectedImages.push(image);
        }
      }

      onChange(newSelectedImages);
    },
    [onChange, selectedImages],
  );

  const removeImages = useCallback(() => {
    revokeFileUrl(selectedImages);
    onChange([]);
  }, [onChange, selectedImages]);

  const addImages = useCallback(
    (files: FileList | File[] | null) => {
      if (!files || files.length === 0) {
        return;
      }

      const newlyAddedImages = Array.from(files).slice(0, maximumFiles).map(fileToFormImage);

      const newSelectedImages = [...selectedImages, ...newlyAddedImages].filter((image) => Boolean(image));

      if (newSelectedImages.length > maximumFiles) {
        const omittedImages = newSelectedImages.splice(0, newSelectedImages.length - maximumFiles);

        revokeFileUrl(omittedImages);
      }

      onChange(newSelectedImages);
    },
    [maximumFiles, onChange, selectedImages],
  );

  const addImage = useCallback((file: File) => addImages([file]), [addImages]);

  const replaceImage = useCallback(
    (oldImage: FormImage, newFile: File) => {
      if (!newFile) {
        return;
      }

      if (oldImage) {
        const newSelectedImages = [];
        for (const image of selectedImages) {
          if (oldImage.url === image.url) {
            revokeFileUrl([image]);
            newSelectedImages.push(fileToFormImage(newFile));
          } else {
            newSelectedImages.push(image);
          }
        }

        onChange(newSelectedImages);
      } else {
        addImage(newFile);
      }
    },
    [onChange, addImage, selectedImages],
  );

  return { addImage, addImages, replaceImage, removeImage, removeImages } as const;
}

export function isImageUploaded(image?: FormImage): image is ImageDto {
  return image ? "id" in image : false;
}

function revokeFileUrl(images: FormImage[]) {
  for (const image of images) {
    if (!isImageUploaded(image)) {
      URL.revokeObjectURL(image.url);
    }
  }
}

function fileToFormImage(file: File): InputFile {
  return {
    file,
    url: URL.createObjectURL(file),
  };
}
